import React, {FC, ReactNode} from "react"
import styled from "styled-components"

import onEnter from "@irati/utils/onEnter"
import {spacingCss, SpacingProps} from "../../global/css"

/** @author: Richard 1/3/2018
 *
 *  @desc: To make the website accessible, we want to show focus borders on focus via keyboard.
 *  However, this looks ugly when you click and see it. This component does some css/html magic
 *  and handles it.
 *
 *  https://stackoverflow.com/questions/31402576/enable-focus-only-on-keyboard-use-or-tab-press
 */

interface Inline extends SpacingProps {
  inline: boolean,
  onKeyPress?(e: any): void
}

const InnerSpan = styled.span<Inline>`
  display: ${({inline}) => (inline ? "inline-block" : "block")};
`

const Outer = styled.span<Inline>`
  cursor: pointer;
  display: ${({inline}) => (inline ? "inline-block" : "block")};

  &:focus,
  ${InnerSpan}:focus {
    outline: none;
  }

  &:focus > ${InnerSpan} {
    box-shadow: 0 0 0 2px #015fcc;
  }
  ${spacingCss}
`

export interface FocusOnKeyboardProps extends Partial<HTMLSpanElement> {
  children?: ReactNode,
  inline?: boolean,
  className?: string,
  onClick(): void
}

const FocusOnKeyboard: FC<FocusOnKeyboardProps> = ({children = null, inline = true, className = "", ...props}) => {
  return (
    <Outer inline={inline} role="button" tabIndex={0} onKeyPress={(e: any) => onEnter(e, props.onClick)} {...props}>
      <InnerSpan inline={inline} className={className} tabIndex={-1}>
        {children}
      </InnerSpan>
    </Outer>
  )
}

export default FocusOnKeyboard
